import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, TextField, Alert, CircularProgress } from '@mui/material';
import sceneService from '../../utils/apiRequests/sceneService';
import ReusableModal from '../Modal/ReusableModal';
import useThemeStyles from '../../hooks/useThemeStyles';

const EditSceneModal = ({ open, onClose, scene, chapterName, onSceneUpdated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { colors } = useThemeStyles();

  useEffect(() => {
    if (open && scene) {
      setName(scene.name ?? '');
      setDescription(scene.description ?? '');
      setError('');
    }
  }, [open, scene]);

  const handleClose = () => {
    if (loading) return;
    setError('');
    onClose();
  };

  const handleSave = async () => {
    const sceneId = scene?.id || scene?._id;
    if (!sceneId) {
      setError('Unable to determine scene identifier.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
      };

      const response = await sceneService.updateScene(sceneId, payload);

      if (response?.error) {
        const msg =
          response.error?.response?.data?.error ||
          response.error?.message ||
          'Failed to update scene. Please try again.';
        setError(msg);
        setLoading(false);
        return;
      }

      const updated = response?.data?.scene ?? { ...scene, ...payload };

      onSceneUpdated(updated);
      setLoading(false);
      onClose();
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to update scene. Please try again.';
      setError(message);
      setLoading(false);
    }
  };

  const outlineFieldSx = {
    '& .MuiInputLabel-root': { color: colors?.text?.secondary ?? 'text.secondary' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    input: { color: colors?.text?.primary ?? 'text.primary' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors?.border?.subtle ?? 'divider' },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
    },
  };

  const outlineMultilineSx = {
    '& .MuiInputLabel-root': { color: colors?.text?.secondary ?? 'text.secondary' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiInputBase-input': { color: colors?.text?.primary ?? 'text.primary' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors?.border?.subtle ?? 'divider' },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
    },
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title="Edit Scene"
      width={560}
      showCloseButton
      ariaLabelledby="edit-scene-modal-title"
      ariaDescribedby="edit-scene-modal-description"
      contentSx={{ gap: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      <Box sx={{ flexShrink: 0 }}>
        {chapterName ? (
          <Typography
            id="edit-scene-modal-description"
            variant="body2"
            sx={{
              color: colors?.text?.secondary ?? 'text.secondary',
              textAlign: 'center',
              mb: 2,
            }}
          >
            In: {chapterName}
          </Typography>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 120,
          pr: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors?.border?.subtle ?? 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: colors?.border?.primary ?? 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        <TextField
          label="Scene name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          disabled={loading}
          placeholder="e.g. Opening, confrontation"
          sx={outlineFieldSx}
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          disabled={loading}
          placeholder="What happens in this scene"
          sx={outlineMultilineSx}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${colors?.border?.subtle ?? 'divider'}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{
              color: colors?.text?.secondary ?? 'text.secondary',
              borderColor: colors?.border?.subtle ?? 'divider',
              '&:hover': {
                color: colors?.text?.primary ?? 'text.primary',
                borderColor: colors?.border?.primary ?? 'primary.main',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined
            }
            sx={{ px: 2.5, py: 0.75, minWidth: 120 }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </ReusableModal>
  );
};

EditSceneModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  scene: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  chapterName: PropTypes.string,
  onSceneUpdated: PropTypes.func.isRequired,
};

EditSceneModal.defaultProps = {
  scene: null,
  chapterName: '',
};

export default EditSceneModal;
